import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Ban, Check, Clock, Loader2, RotateCcw, Search, ShieldCheck,
  Sliders, UserCheck, Users, X,
} from 'lucide-react';
import { AdminSidebar } from '../../components/layout/AdminSidebar';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog } from '../../components/ui/dialog';
import { api, type AccountStatus, type AdminUser } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { cn, formatDate } from '../../lib/utils';

type Filter = 'ALL' | AccountStatus;

const FILTERS: Array<{ label: string; value: Filter }> = [
  { label: 'Pending approval', value: 'WAITING_APPROVAL' },
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Suspended', value: 'SUSPENDED' },
  { label: 'Everyone', value: 'ALL' },
];

function StatusCell({ status }: { status: AccountStatus }) {
  if (status === 'APPROVED') {
    return <Badge variant="success"><Check size={9} className="mr-1" />Approved</Badge>;
  }
  if (status === 'WAITING_APPROVAL') {
    return <Badge variant="warning"><Clock size={9} className="mr-1" />Pending</Badge>;
  }
  return <Badge variant="danger"><Ban size={9} className="mr-1" />Suspended</Badge>;
}

function UserCard({
  user, isSelf, onChanged, onError,
}: {
  user: AdminUser;
  isSelf: boolean;
  onChanged: (updated: AdminUser) => void;
  onError: (message: string) => void;
}) {
  const [busy, setBusy] = useState<null | 'status' | 'admin'>(null);
  const [confirmAdmin, setConfirmAdmin] = useState(false);

  const act = async (which: 'status' | 'admin', action: () => Promise<AdminUser>) => {
    setBusy(which);
    try {
      onChanged(await action());
    } catch (e: any) {
      onError(e.message);
    } finally {
      setBusy(null);
    }
  };

  const setStatus = (accountStatus: AccountStatus) =>
    act('status', () => api.admin.setAccountStatus(user.id, accountStatus));

  const overrideCount = user.disabled.length;

  return (
    <>
      <div className="border border-border rounded-lg bg-card px-4 py-3">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-ink truncate">{user.name ?? user.email}</span>
              {user.isSystemAdmin && <Badge variant="info"><ShieldCheck size={9} className="mr-1" />Admin</Badge>}
              {isSelf && <Badge>You</Badge>}
            </div>
            <div className="flex items-center gap-2.5 mt-1 flex-wrap">
              {user.name && <span className="text-[11px] text-ink-faint">{user.email}</span>}
              <span className="text-[11px] text-ink-faint">joined {formatDate(user.createdAt)}</span>
              <Link
                to="/admin/features"
                className="text-[11px] text-orange-500 hover:text-orange-700 transition-colors flex items-center gap-1"
              >
                <Sliders size={9} />
                {overrideCount === 0 ? 'full access' : `${overrideCount} override${overrideCount === 1 ? '' : 's'}`}
              </Link>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <StatusCell status={user.accountStatus} />
          </div>
        </div>

        <div className="flex items-center gap-2 mt-3 flex-wrap">
          {user.accountStatus === 'WAITING_APPROVAL' && (
            <Button variant="primary" size="sm" disabled={busy !== null} onClick={() => setStatus('APPROVED')}>
              {busy === 'status' ? <Loader2 size={12} className="mr-1.5 animate-spin" /> : <UserCheck size={12} className="mr-1.5" />}
              Approve account
            </Button>
          )}

          {user.accountStatus === 'APPROVED' && !isSelf && (
            <Button size="sm" disabled={busy !== null} onClick={() => setStatus('SUSPENDED')}>
              <Ban size={12} className="mr-1.5" />Suspend
            </Button>
          )}

          {user.accountStatus === 'SUSPENDED' && (
            <Button variant="primary" size="sm" disabled={busy !== null} onClick={() => setStatus('APPROVED')}>
              <RotateCcw size={12} className="mr-1.5" />Reinstate
            </Button>
          )}

          <Button
            size="sm"
            disabled={busy !== null || isSelf}
            title={
              isSelf
                ? 'You cannot change your own admin access — another admin has to do it'
                : user.isSystemAdmin
                  ? 'Revoke access to this console'
                  : 'Grant access to this console'
            }
            onClick={() => {
              if (user.isSystemAdmin) act('admin', () => api.admin.setSystemAdmin(user.id, false));
              else setConfirmAdmin(true);
            }}
          >
            {busy === 'admin' ? (
              <Loader2 size={12} className="mr-1.5 animate-spin" />
            ) : (
              <ShieldCheck size={12} className="mr-1.5" />
            )}
            {user.isSystemAdmin ? 'Revoke admin' : 'Make admin'}
          </Button>
        </div>
      </div>

      {/* Granting is confirmed; revoking isn't. Granting hands over account control and the
          ability to grant it onward, which is the direction worth a second look. */}
      <Dialog open={confirmAdmin} onClose={() => setConfirmAdmin(false)} title="Grant system-admin access">
        <div className="space-y-4">
          <p className="text-sm text-ink-muted">
            <span className="font-medium text-ink">{user.name ?? user.email}</span> will be able to approve
            and suspend accounts, edit anyone's feature access, and grant this same permission to others.
          </p>
          <div className="flex justify-end gap-2">
            <Button onClick={() => setConfirmAdmin(false)}>Cancel</Button>
            <Button
              variant="primary"
              onClick={() => {
                setConfirmAdmin(false);
                act('admin', () => api.admin.setSystemAdmin(user.id, true));
              }}
            >
              Grant access
            </Button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

export default function AdminUsers() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [filter, setFilter] = useState<Filter>('ALL');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api.admin
      .listUsers()
      .then((list) => { setUsers(list); setError(null); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const pendingCount = users.filter((u) => u.accountStatus === 'WAITING_APPROVAL').length;

  // Default to the pending queue when there's something waiting — that's the reason to open
  // this page — but only on the first load, so it can't yank the filter back later.
  const [autoFiltered, setAutoFiltered] = useState(false);
  useEffect(() => {
    if (!loading && !autoFiltered) {
      setAutoFiltered(true);
      if (pendingCount > 0) setFilter('WAITING_APPROVAL');
    }
  }, [loading, autoFiltered, pendingCount]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return users
      .filter((u) => filter === 'ALL' || u.accountStatus === filter)
      .filter(
        (u) =>
          !needle ||
          u.email.toLowerCase().includes(needle) ||
          (u.name ?? '').toLowerCase().includes(needle),
      );
  }, [users, filter, search]);

  const applyUpdate = (updated: AdminUser) =>
    setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));

  return (
    <div className="flex h-screen overflow-hidden bg-surface">
      <AdminSidebar />

      <div className="pl-56 flex-1 h-full overflow-y-auto">
        <div className="max-w-3xl mx-auto px-7 py-7">
          <div className="mb-5">
            <h1 className="text-lg font-bold text-ink flex items-center gap-2">
              <Users size={16} className="text-ink-faint" />Accounts
            </h1>
            <p className="text-xs text-ink-muted mt-1 max-w-2xl">
              Approve new sign-ups, suspend accounts, and grant access to this console. Feature access
              is edited under <Link to="/admin/features" className="text-orange-500 hover:text-orange-700">Feature Permissions</Link>.
            </p>
          </div>

          {pendingCount > 0 && (
            <p className="mb-4 text-xs text-warning-text bg-warning-bg border border-warning-ring rounded px-3 py-2 flex items-center gap-1.5">
              <Clock size={12} />
              {pendingCount} account{pendingCount === 1 ? '' : 's'} awaiting approval — they can't sign in until approved.
            </p>
          )}

          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={cn(
                  'px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors border',
                  filter === f.value
                    ? 'bg-ink text-surface border-ink'
                    : 'bg-card text-ink-muted border-border hover:text-ink',
                )}
              >
                {f.label}
                {f.value === 'WAITING_APPROVAL' && pendingCount > 0 && ` (${pendingCount})`}
              </button>
            ))}
            <div className="relative ml-auto">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search…"
                className="pl-7 pr-3 py-1.5 text-xs bg-card border border-border rounded-md outline-none focus:border-orange-400 placeholder:text-ink-faint text-ink w-40"
              />
            </div>
          </div>

          {error && (
            <p className="mb-4 text-xs text-danger-text bg-danger-bg border border-danger-ring rounded px-3 py-2 flex items-start gap-1.5">
              <X size={12} className="mt-px flex-shrink-0" />{error}
            </p>
          )}

          {loading ? (
            <p className="text-xs text-ink-faint py-8 text-center">Loading…</p>
          ) : visible.length === 0 ? (
            <div className="border border-dashed border-border rounded-lg py-12 text-center">
              <p className="text-sm text-ink-secondary mb-1">No accounts here</p>
              <p className="text-xs text-ink-faint">Try another filter.</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {visible.map((user) => (
                <UserCard
                  key={user.id}
                  user={user}
                  isSelf={currentUser?.id === user.id}
                  onChanged={applyUpdate}
                  onError={setError}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
